import { FC } from 'react';

import { useGetBillsPresent, useHasBillPermissions } from 'app/api/hooks/paperwork/v1/bills';
import AppLayout, { LayoutProps, Link } from 'app/components/layout/AppLayout';
import { getAppFlags } from 'app/config/flags';
import { payorRoutes, routes } from 'app/constants/routes';
import { PayorProfileType } from 'app/constants/user';
import { useGetPayorProfileSetting } from 'app/hooks/payor';
import { useQueryString } from 'app/state/NavigationContext/hooks';
import { removeItemFromArray } from 'app/utils/lib/array';

// Component Definition
const PayorRoutesLayout: FC<LayoutProps> = props => {
  const creditNotesQueryString = useQueryString('payorCreditNotes');
  const invoicesQueryString = useQueryString('payorInvoices');
  const loadsQueryString = useQueryString('payorLoads');
  const paymentsQueryString = useQueryString('payorPayments');
  const billsQueryString = useQueryString('payorPaperwork');
  const relationshipsQueryString = useQueryString('payorRelationships');

  const isCreditNotesEnabled = useGetPayorProfileSetting(PayorProfileType.EnableCreditNotes, { format: 'boolean' });
  const isLoadsEnabled = useGetPayorProfileSetting(PayorProfileType.EnableLoads, { format: 'boolean' });

  const hasBillPermissions = useHasBillPermissions({ allowUnacceptedTerms: true });
  const billsPresent = useGetBillsPresent();

  const hasBills = billsPresent.data?.hasBills;

  const flags = getAppFlags();

  let links: Link[] = [
    {
      activePaths: [`/${routes.payor}/${payorRoutes.dashboard}`],
      iconName: 'home',
      id: 'dashboard',
      label: 'Dashboard',
      to: `/${routes.payor}/${payorRoutes.dashboard}`,
    },
    {
      activePaths: [`/${routes.payor}/${payorRoutes.relationships}`],
      iconName: 'person',
      id: 'carriers',
      label: 'Carriers',
      to: `/${routes.payor}/${payorRoutes.relationships}${relationshipsQueryString}`,
    },
    {
      activePaths: [`/${routes.payor}/${payorRoutes.creditNotes}`],
      iconName: 'report',
      id: 'credit-notes',
      label: 'Credit Notes',
      to: `/${routes.payor}/${payorRoutes.creditNotes}${creditNotesQueryString}`,
    },
    {
      activePaths: [`/${routes.payor}/${payorRoutes.settlements}`],
      iconName: 'receipt',
      id: 'settlements',
      label: 'Settlements',
      to: `/${routes.payor}/${payorRoutes.settlements}${billsQueryString}`,
    },
    {
      activePaths: [`/${routes.payor}/${payorRoutes.invoices}`],
      iconName: 'file',
      id: 'invoices',
      label: 'Invoices',
      to: `/${routes.payor}/${payorRoutes.invoices}${invoicesQueryString}`,
    },
    {
      activePaths: [`/${routes.payor}/${payorRoutes.payments}`],
      iconName: 'money',
      id: 'payments',
      label: 'Payments',
      to: `/${routes.payor}/${payorRoutes.payments}${paymentsQueryString}`,
    },
    {
      activePaths: [`/${routes.payor}/${payorRoutes.loads}`],
      iconName: 'truck',
      id: 'loads',
      label: 'Loads',
      to: `/${routes.payor}/${payorRoutes.loads}${loadsQueryString}`,
    },
  ];

  if (!isCreditNotesEnabled) {
    const removeIndex = links.findIndex(link => link.label === 'Credit Notes');

    links = removeItemFromArray(links, removeIndex);
  }
  if (!isLoadsEnabled) {
    const removeIndex = links.findIndex(link => link.label === 'Loads');

    links = removeItemFromArray(links, removeIndex);
  }

  if (!flags.isPaperworkInProduction || !hasBillPermissions || !hasBills) {
    const settlementsIndex = links.findIndex(link => link.label === 'Settlements');
    if (settlementsIndex !== -1) {
      links.splice(settlementsIndex,
        1);
    }
  }

  return (
    <AppLayout
      body={props.body}
      logoPath={`/${routes.payor}/${payorRoutes.dashboard}`}
      navLinks={links}
    />
  );
};

export default PayorRoutesLayout;
